import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useSubscription } from '../../hooks/useSubscription';
import { useSelector, useDispatch } from 'react-redux';
import subscriptionService from '../../services/subscriptionService';
import { createSubscriptionPoller } from '../../utils/subscriptionUtils';
import { fetchUserQuota, fetchUserSubscription } from '../../store/subscriptionSlice';
import { extractPlanPriceId } from '../../utils/subscriptionUtils';

const PaymentForm = ({ plan, actionType, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { handleStartBillingSubscription } = useSubscription();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);

  const isFree = !plan.price || plan.price.amount === 0;
  const isDowngrade = actionType === 'downgrade';

  // Auto-capture payment method when card details change
  const handleCardChange = async (event) => {
    if (!stripe || !elements) return;

    // Clear any previous errors
    setPaymentError('');

    // If card is complete and valid, automatically create payment method
    if (event.complete && !event.error) {
      setIsCapturing(true);
      
      try {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement),
        });

        if (error) {
          setPaymentError(error.message);
          setPaymentMethodId(null);
        } else {
          // Successfully created payment method
          setPaymentMethodId(paymentMethod.id);
          setPaymentError('');
        }
      } catch (error) {
        setPaymentError('Failed to create payment method');
        setPaymentMethodId(null);
      } finally {
        setIsCapturing(false);
      }
    } else if (event.error) {
      setPaymentError(event.error.message);
      setPaymentMethodId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (isFree) {
      await handleFreeSubscription();
      return;
    }

    if (!stripe || !elements || !paymentMethodId) {
      setPaymentError('Please complete your card details');
      return;
    }

    await handlePaidSubscription();
  };

  const handleFreeSubscription = async () => {
    setIsProcessing(true);
    setPaymentError('');

    try {
      // For free plans, use the dedicated start-free endpoint
      const response = await subscriptionService.startFreeSubscription(user.id, plan.planCode);
      
      if (response && response.success) {
        setIsProcessing(false);
        
        // Refresh user quota after successful subscription
        if (user?.id) {
          dispatch(fetchUserQuota(user.id));
        }
        
        onSuccess({
          type: 'free',
          subscription: response.data,
          message: 'Free subscription started successfully!'
        });
      } else {
        throw new Error('Failed to start free subscription');
      }
    } catch (error) {
      setPaymentError(error.message || 'Failed to start free subscription');
      onError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaidSubscription = async () => {
    setIsProcessing(true);
    setPaymentError('');

    try {
      // Extract plan price ID using consolidated utility
      const planPriceId = extractPlanPriceId(plan);
      
      // Check if we have a valid planPriceId
      if (!planPriceId) {
        // For plans without Stripe price ID, treat as free plan
        await handleFreeSubscription();
        return;
      }
      
      // Use the useSubscription hook instead of calling service directly
      const result = await handleStartBillingSubscription({
        userId: user.id,
        planCode: plan.planCode,
        planPriceId: planPriceId,
        paymentMethodId: paymentMethodId, // Use the captured payment method
        storageLimitSeconds: plan.storageLimitSeconds,
      });
      
      // Extract response from Redux thunk result
      const response = result.payload;
      
      console.log('PaymentForm - Backend response:', response);
      console.log('PaymentForm - Response data:', response?.data);
      console.log('PaymentForm - Client secret:', response?.clientSecret || response?.data?.clientSecret);
      
      if (response && (response.success || response.clientSecret)) {
        // 2) If invoice needs SCA, confirm it
        const clientSecret = response.clientSecret || response.data?.clientSecret;
        if (clientSecret) {
          console.log('PaymentForm - Confirming payment with clientSecret:', clientSecret);
          console.log('PaymentForm - Using paymentMethodId:', paymentMethodId);
          
          // Use paymentMethodId if available, otherwise fallback to CardElement
          const paymentMethodOptions = paymentMethodId 
            ? { payment_method: paymentMethodId }
            : { payment_method: { card: elements.getElement(CardElement) } };
            
          const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, paymentMethodOptions);
          
          console.log('PaymentForm - Payment confirmation result:', { error, paymentIntent });
          
          if (error) {
            console.error('PaymentForm - Payment confirmation error:', error);
            console.error('PaymentForm - Error code:', error.code);
            console.error('PaymentForm - Payment intent status:', error.payment_intent?.status);
            throw new Error(`Payment confirmation failed: ${error.message}`);
          }
          
          console.log('PaymentForm - Payment intent status:', paymentIntent?.status);
          
          if (paymentIntent.status === 'succeeded') {
            // 3) Payment succeeded on Stripe side, now poll for webhook processing
            try {
              // According to the flow: webhook needs time to process invoice.paid + customer.subscription.updated
              // Wait a moment for webhook to process the Stripe events
              await new Promise(resolve => setTimeout(resolve, 3000));
              
              const finalSubscription = await createSubscriptionPoller(
                () => subscriptionService.getUserSubscription(user.id).then(res => res.data),
                20, // max attempts (100 seconds total) - webhooks can take time
                2000 // 2 second intervals for responsive updates
              );
              
              if (finalSubscription && finalSubscription.status === 'ACTIVE') {
                // Refresh user quota and subscription data after successful subscription
                if (user?.id) {
                  // Dispatch async thunks properly
                  Promise.all([
                    dispatch(fetchUserQuota(user.id)),
                    dispatch(fetchUserSubscription(user.id))
                  ]);
                }
                
                setIsProcessing(false);
                onSuccess({
                  type: 'paid',
                  subscription: finalSubscription,
                  paymentIntent,
                  message: 'Payment successful! Your subscription is now ACTIVE.'
                });
              } else if (finalSubscription === null) {
                // Webhook failed completely - subscription was never created
                setIsProcessing(false);
                onSuccess({
                  type: 'paid',
                  subscription: response,
                  paymentIntent,
                  message: 'Payment successful! Your subscription is being processed. If you don\'t see it in a few minutes, please contact support.'
                });
              } else {
                // Webhook didn't complete in time, but payment succeeded
                setIsProcessing(false);
                onSuccess({
                  type: 'paid',
                  subscription: finalSubscription || response,
                  paymentIntent,
                  message: 'Payment successful! Your subscription is being activated (webhook processing).'
                });
              }
            } catch (pollError) {
              // Still show success since payment went through
              setIsProcessing(false);
              onSuccess({
                type: 'paid',
                subscription: response,
                paymentIntent,
                message: 'Payment successful! Your subscription is being activated.'
              });
            }
          } else {
            // Handle other payment intent statuses
            setIsProcessing(false);
            onSuccess({
              type: 'paid',
              subscription: response,
              paymentIntent,
              message: `Payment status: ${paymentIntent.status}. We'll notify you when it's confirmed.`
            });
          }
        } else {
          // No client secret needed - direct success
          console.log('PaymentForm - No client secret needed, subscription created directly');
          console.log('PaymentForm - Response data:', response);
          setIsProcessing(false);
          onSuccess({
            type: 'paid',
            subscription: response,
            message: 'Subscription started successfully!'
          });
        }
      } else {
        throw new Error('Failed to start subscription');
      }
    } catch (error) {
      setPaymentError(error.message || 'Failed to process payment');
      onError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // For downgrades, we might not need payment method
  if (isDowngrade && isFree) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">
            <strong>Plan Change:</strong> You're switching to a free plan. No payment is required.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleFreeSubscription}
            disabled={isProcessing}
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? 'Processing...' : 'Change to Free Plan'}
          </button>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isFree && (
        <div className="space-y-4">
          <div className="border border-gray-300 rounded-lg p-4">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
              onChange={handleCardChange}
            />
          </div>
          
          {isCapturing && (
            <div className="flex items-center gap-2 text-blue-600 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span>Capturing payment method...</span>
            </div>
          )}
          
          {paymentError && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              {paymentError}
            </div>
          )}
          
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800">
              💳 <strong>Auto-capture:</strong> Your payment method will be automatically captured as you type your card details.
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isProcessing || (!isFree && !paymentMethodId)}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
            isFree
              ? 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed'
          }`}
        >
          {isProcessing ? 'Processing...' : 
            isFree ? 'Start Free Subscription' :
            isDowngrade ? 'Change Plan' :
            'Subscribe Now'
          }
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
